import { Alert, Box, Button, Modal, ProgressBar, SpaceBetween, Table } from '@amzn/awsui-components-react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { chunk, isError, pick } from 'lodash';
import { getJamClient } from '@/src/api/sdk/JamClient';
import { useAuth } from '@/src/store/auth.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { downloadObjectAsCsv } from '@/src/utils/download.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Team } from '@/src/types/Team';
import { delay } from '@/src/utils/delay';

export interface ExportTeamsModalProps {
  eventName: string;
  teams: Team[];
}

interface ExportRow {
  teamName: string;
  email: string;
  legacyLogin: string;
}

export const ExportTeamsModal = NiceModal.create((props: ExportTeamsModalProps) => {
  const { eventName, teams } = props;
  const modal = useModal();
  const { t } = useTranslation();
  const { authClient } = useAuth();
  const jamClient = getJamClient(authClient);
  const { addSuccessFlashbar } = useFlashbars();

  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [exporting, setExporting] = useState(false);
  const [fetchedCount, setFetchedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [exportError, setExportError] = useState('');
  const cancelledRef = useRef(false);

  const selectedMemberCount = selectedTeams.reduce((sum, team) => sum + (team.members || []).length, 0);

  const handleDismiss = () => {
    if (exporting) {
      return;
    }

    modal.hide();
  };

  const handleCancel = () => {
    cancelledRef.current = true;
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError('');
    setFetchedCount(0);
    cancelledRef.current = false;

    const allMembers: { teamName: string; vibeId: string; legacyLogin: string }[] = [];

    for (const team of selectedTeams) {
      for (const member of team.members || []) {
        if (member.userId && member.vibeId && team.name) {
          allMembers.push({ teamName: team.name, legacyLogin: member.userId, vibeId: member.vibeId });
        }
      }
    }

    if (allMembers.length === 0) {
      setExportError(t(i18nKeys.participants.labels.exportTeamsNoData));
      setExporting(false);
      return;
    }

    setTotalCount(allMembers.length);

    const results: any[] = [];
    const BATCH_SIZE = 25;

    for (const batch of chunk(allMembers, BATCH_SIZE)) {
      if (cancelledRef.current) {
        setExporting(false);
        return;
      }

      let response: any;
      const vibeIds = batch.map((m) => m.vibeId);

      for (let attempt = 0; attempt <= 3 && !response; attempt++) {
        response = await jamClient.fetchUserEmails({ eventName, vibeIds });

        const isThrottled = isError(response) && (response as any)?.$metadata?.httpStatusCode === 429;

        if (isThrottled && attempt < 3) {
          response = undefined;
          await delay(2000 * Math.pow(2, attempt));
        }
      }

      if (isError(response)) {
        for (const member of batch) {
          results.push({
            teamName: member.teamName,
            email: '',
            legacyLogin: member.legacyLogin,
            failed: true,
          });
        }

        setFetchedCount((prev) => prev + batch.length);
        continue;
      }

      const emailMap = new Map<string, string>();

      for (const entry of response.successful || []) {
        if (entry.vibeId && entry.email) {
          emailMap.set(entry.vibeId, entry.email);
        }
      }

      const errorSet = new Set<string>();

      for (const entry of response.errored || []) {
        if (entry.vibeId) {
          errorSet.add(entry.vibeId);
        }
      }

      for (const member of batch) {
        const email = emailMap.get(member.vibeId);

        results.push({
          teamName: member.teamName,
          email: email || '',
          legacyLogin: member.legacyLogin,
          failed: !email && errorSet.has(member.vibeId),
        });
      }

      setFetchedCount((prev) => prev + batch.length);
    }

    const rows: ExportRow[] = results.map((r) => pick(r, ['teamName', 'email', 'legacyLogin']));
    const hasError = !!results.find((r) => r.failed);

    if (rows.length > 0) {
      downloadObjectAsCsv(rows, `teams-export-${eventName}`);
    }

    if (hasError) {
      setExportError(t(i18nKeys.participants.labels.exportTeamsError));
    } else {
      const teamCount = new Set(rows.map((r) => r.teamName)).size;
      addSuccessFlashbar(t(i18nKeys.participants.labels.exportTeamsSuccess, { count: teamCount }));

      modal.hide();
    }

    setExporting(false);
  };

  return (
    <Modal
      visible={modal.visible}
      onDismiss={handleDismiss}
      header={t(i18nKeys.participants.headers.exportTeams)}
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {exporting ? (
              <Button variant="normal" onClick={handleCancel} data-testid="export-teams-cancel-btn">
                {t(i18nKeys.participants.buttons.exportTeamsCancelExport)}
              </Button>
            ) : (
              <Button variant="link" onClick={handleDismiss}>
                {t(i18nKeys.general.cancel)}
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => void handleExport()}
              disabled={selectedTeams.length === 0 || exporting}
              loading={exporting}
              data-testid="export-teams-confirm-btn"
            >
              {t(i18nKeys.participants.buttons.exportTeams)}
            </Button>
          </SpaceBetween>
        </Box>
      }
      data-testid="export-teams-modal"
    >
      <SpaceBetween size="m">
        <Box>{t(i18nKeys.participants.labels.exportTeamsDescription)}</Box>

        <div style={{ maxHeight: '600px', overflow: 'auto' }}>
          <Box padding={{ right: 'm' }}>
            <Table
              items={teams}
              selectionType="multi"
              selectedItems={selectedTeams}
              onSelectionChange={({ detail }) => setSelectedTeams(detail.selectedItems)}
              trackBy="name"
              variant="borderless"
              stickyHeader
              columnDefinitions={[
                {
                  id: 'teamName',
                  header: t(i18nKeys.participants.headers.teamName),
                  cell: (item) => item.name,
                },
                {
                  id: 'members',
                  header: t(i18nKeys.participants.headers.teamMembers),
                  cell: (item) => String((item.members || []).length),
                },
              ]}
              header={
                selectedTeams.length > 0
                  ? t(i18nKeys.participants.labels.exportTeamsSelectedSummary, {
                      teamCount: selectedTeams.length,
                      memberCount: selectedMemberCount,
                    })
                  : undefined
              }
              data-testid="export-teams-selection-table"
            />
          </Box>
        </div>

        {exporting && (
          <ProgressBar
            value={totalCount > 0 ? Math.round((fetchedCount / totalCount) * 100) : 0}
            label={t(i18nKeys.participants.labels.exportTeamsFetchingEmails)}
            data-testid="export-teams-progress"
          />
        )}

        {exportError && <Alert type="error">{exportError}</Alert>}
      </SpaceBetween>
    </Modal>
  );
});
